/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { ContextualizedWidgetReports } from '../../../../Fhir/fhirSlice';
import { Rule, State } from './types';

class RuleEngineState {
  private state: State;

  constructor() {
    this.state = {
      labs: {
        done: [],
        notDone: [],
      },
      imaging: {
        done: [],
        notDone: [],
      },
      procedures: {
        done: [],
        notDone: [],
      },
    };
  }

  update(newState: Partial<State>) {
    this.state = { ...this.state, ...newState };
  }

  getState(): State {
    return this.state;
  }
}

export interface RuleEngineData {
  contextualizedWidgetReports: ContextualizedWidgetReports;
  dateRange?: number;
}

export class RuleEngine {
  private rules: Rule[];
  private state: RuleEngineState;

  constructor(rules: Rule[]) {
    this.rules = rules;
    this.state = new RuleEngineState();
  }

  execute(data: RuleEngineData): State {
    for (const rule of this.rules) {
      const ruleBasedData = JSON.parse(JSON.stringify(data)) as RuleEngineData;
      // Only execute the rule for the widgets that match the rule type
      ruleBasedData.contextualizedWidgetReports = this.filterContextualizedWidgets(ruleBasedData, rule.type);
      ruleBasedData.contextualizedWidgetReports = this.filterReports(ruleBasedData);
      const result = rule.execute(ruleBasedData);
      this.state.update(result);
    }
    return this.state.getState();
  }

  // This function filters the contextualized widgets based on the defined parameters
  // and returns the filtered contextualized widget reports
  private filterContextualizedWidgets(data: RuleEngineData, ruleType: string): ContextualizedWidgetReports {
    const contextualizedWidgets = data.contextualizedWidgetReports;
    const hasMatchingWidgetType = (widgetId: string) => contextualizedWidgets[widgetId]?.widget?.type === ruleType;
    return Object.keys(contextualizedWidgets)
      .filter((widgetId) => hasMatchingWidgetType(widgetId))
      .reduce((obj: ContextualizedWidgetReports, widgetId: string) => {
        obj[widgetId] = contextualizedWidgets[widgetId]!;
        return obj;
      }, {});
  }

  // This function filters the reports based on the defined parameters
  // and returns the filtered contextualized widget reports
  private filterReports(data: RuleEngineData): ContextualizedWidgetReports {
    // If the date range is not defined or is All (0), return the contextualized widget reports as is
    if (!data.dateRange) return data.contextualizedWidgetReports;

    const contextualizedWidgets = data.contextualizedWidgetReports;
    const dateRangeInMonths = data.dateRange / 30;
    return Object.keys(contextualizedWidgets)
      .map((widgetId) => {
        const { widget, reports } = contextualizedWidgets[widgetId]!;
        // If the widget has a lookback time of -1 aka lifetime, return all reports without filtering
        if (widget?.lookbackTimeMonths === -1) return { widgetId, reports };
        const filteredReports = reports.filter((report) => {
          const ageMonths = report.ageMonths;
          return !ageMonths || ageMonths <= dateRangeInMonths;
        });
        return { widgetId, reports: filteredReports };
      })
      .reduce((obj: ContextualizedWidgetReports, { widgetId, reports }) => {
        obj[widgetId] = { widget: contextualizedWidgets[widgetId]?.widget, reports };
        return obj;
      }, {});
  }
}
